import { ErrorModal } from "@dexteel/mesf-core";
import {
	Checkbox,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { getNextLocationAssets } from "../repositories/NextLocationRepository";

// Destinations offered on a work order: every line that can receive material
// plus Ship, which is a destination without an asset.
const useNextLocationOptions = (onError?: (error: Error) => void) => {
	const { data: assets } = useQuery({
		queryKey: ["nextLocationAssets"],
		queryFn: getNextLocationAssets,
		staleTime: 5 * 60 * 1000,
		onError,
	});

	return useMemo(
		() => [...(assets ?? []).map((a) => a.AssetName), "Ship"],
		[assets],
	);
};

type Props = {
	value: string[];
	onChange: (values: string[]) => void;
	disabled?: boolean;
	// Calendar layout: shrunk notched label + fixed 40px height.
	dense?: boolean;
};

// Legacy tokens (old coarse labels) stay selected and removable, but can
// never be re-added once unchecked.
export const NextLocationSelector = ({
	value,
	onChange,
	disabled = false,
	dense = false,
}: Props) => {
	const [error, setError] = useState("");
	const options = useNextLocationOptions((e) => setError(e.message));
	const legacySelected = value.filter((v) => !options.includes(v));

	return (
		<>
			<FormControl size="small" fullWidth>
				<InputLabel shrink={dense || undefined}>Next Location</InputLabel>
				<Select
					multiple
					size="small"
					notched={dense || undefined}
					label="Next Location"
					value={value}
					onChange={(e) => {
						const values = e.target.value as string[];
						onChange([
							...options.filter((o) => values.includes(o)),
							...values.filter((v) => !options.includes(v)),
						]);
					}}
					renderValue={(values) => (values as string[]).join(", ")}
					disabled={disabled}
					sx={dense ? { height: 40 } : undefined}
				>
					{[...options, ...legacySelected].map((opt) => (
						<MenuItem key={opt} value={opt} dense>
							<Checkbox
								size="small"
								checked={value.includes(opt)}
								sx={{ py: 0, mr: 0.5 }}
							/>
							{opt}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading next locations"
			/>
		</>
	);
};
